import type { CSSProperties } from "react";

/**
 * Skeleton da página de Perfil espelhando o layout real da ilha (hero, stat tiles,
 * abas, filtros e grid). Fica na tela enquanto o chunk e os dados carregam.
 */
export default function PerfilSkeleton() {
  const shimmer: CSSProperties = {
    background: "linear-gradient(90deg,#ececec 0%,#f7f7f7 50%,#ececec 100%)",
    backgroundSize: "200% 100%",
    animation: "reactPerfilSkeletonShimmer 1.2s ease-in-out infinite",
  };
  const card: CSSProperties = {
    background: "#fff",
    border: "1px solid #e8e8e8",
  };

  const bar = (
    width: CSSProperties["width"],
    height: number,
    extra?: CSSProperties
  ): CSSProperties => ({
    ...shimmer,
    width,
    height,
    borderRadius: 6,
    ...extra,
  });

  return (
    <div aria-hidden="true">
      <style>{`
        @keyframes reactPerfilSkeletonShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .ap-pf-skel-hero { padding: 24px; }
        .ap-pf-skel-avatar { width: 96px; height: 96px; }
        .ap-pf-skel-hero-info { flex: 1 1 0; min-width: 0; }
        .ap-pf-skel-hero-acoes {
          margin-left: auto;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .ap-pf-skel-stats {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
          margin-top: 16px;
        }
        .ap-pf-skel-stat { border-radius: 10px; padding: 14px 16px; }
        /* Espelha o hero real: abaixo de 800px as ações viram uma linha própria */
        @media (max-width: 800px) {
          .ap-pf-skel-hero-acoes { margin-left: 0; flex-direction: row; width: 100%; }
          .ap-pf-skel-hero-acoes > div { flex: 1; }
        }
        @media (max-width: 575px) {
          .ap-pf-skel-hero { padding: 18px; }
          .ap-pf-skel-avatar { width: 72px; height: 72px; }
          .ap-pf-skel-stats { gap: 8px; }
          .ap-pf-skel-stat { padding: 10px 12px; }
        }
      `}</style>

      {/* Hero: avatar + nome/email/badge + ações à direita */}
      <div
        className="ap-pf-skel-hero"
        style={{
          ...card,
          display: "flex",
          alignItems: "center",
          gap: 20,
          flexWrap: "wrap",
          borderRadius: 12,
        }}
      >
        <div
          className="ap-pf-skel-avatar"
          style={{
            ...shimmer,
            borderRadius: "50%",
            flexShrink: 0,
          }}
        />
        <div className="ap-pf-skel-hero-info">
          <div style={bar(190, 22, { maxWidth: "100%" })} />
          <div style={bar(230, 13, { marginTop: 8, maxWidth: "100%" })} />
          <div style={bar(150, 18, { marginTop: 12, borderRadius: 999 })} />
        </div>
        <div className="ap-pf-skel-hero-acoes">
          <div style={bar(120, 30, { borderRadius: 999 })} />
          <div style={bar(120, 30, { borderRadius: 999 })} />
        </div>
      </div>

      {/* Stat tiles: 3 colunas fixas, como na ilha real (inclusive mobile) */}
      <div className="ap-pf-skel-stats">
        {[0, 1, 2].map((i) => (
          <div key={i} className="ap-pf-skel-stat" style={card}>
            <div style={bar("100%", 12, { maxWidth: 100 })} />
            <div style={bar(44, 26, { marginTop: 8 })} />
          </div>
        ))}
      </div>

      {/* Abas */}
      <div
        style={{
          display: "flex",
          gap: 4,
          marginTop: 24,
          borderBottom: "2px solid #eee",
        }}
      >
        <div style={bar(150, 16, { margin: "10px 16px" })} />
        <div style={bar(170, 16, { margin: "10px 16px" })} />
      </div>

      {/* Filtros */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 24,
          flexWrap: "wrap",
          marginTop: 18,
        }}
      >
        <div style={bar(130, 32, { borderRadius: 999 })} />
        <div style={bar(150, 32, { borderRadius: 999 })} />
        <div style={bar(170, 32, { borderRadius: 999 })} />
      </div>

      {/* Grid de cards da garagem */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 16,
          marginTop: 20,
        }}
      >
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{ ...card, borderRadius: 12, overflow: "hidden" }}>
            <div style={{ ...shimmer, aspectRatio: "16 / 9" }} />
            <div style={{ padding: "12px 14px 14px" }}>
              <div style={bar("70%", 14)} />
              <div style={bar("50%", 11, { marginTop: 8 })} />
              <div style={bar("40%", 13, { marginTop: 8 })} />
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <div style={bar(undefined, 29, { flex: 1, borderRadius: 999 })} />
                <div style={bar(84, 29, { borderRadius: 999 })} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
